import { Datasources, Utils } from '@sparrow-marketing/contracts';
import {
  businessRoles,
  employeeCounts,
  industries,
  subscriptions,
} from './onboarding/index';
import { ENV_VARS } from './env-vars';

export const ENVIRONMENTS: { [env: string]: string } = {
  local: 'local',
  development: 'development',
  staging: 'staging',
  production: 'production',
};

export const environment =
  ENVIRONMENTS[ENV_VARS.VITE_APP_BUILD_ENV] || ENVIRONMENTS.development;

export const API_BASE_URL = {
  local: 'http://localhost:4201',
  development: 'https://staging-api.viewmetrics.com',
  staging: 'https://staging-api.viewmetrics.com',
  production: 'https://api.viewmetrics.com',
};

// Kaevon's Business
export const endpoints = () => {
  return {
    authenticationUrl: '/authentication',
    userUrl: '/users',
    businessUrl: '/business',
    accessUrl: '/access',
    billingUrl: '/billing',
    clientUrl: '/clients',
    accountsUrl: '/provider-accounts',
    reportsUrl: '/reports',
    publishReportsUrls: '/publish-report',
    templatesUrl: '/report-templates',
    globalTemplatesUrl: '/global-report-templates',
    authManagementUrl: '/authManagement',
    insightsUrl: '/insights',
    taskUrl: '/task',
    tasklistUrl: '/tasklist',
    demoUrl: '/demo-insights',
    competitorUrl: '/competitors',
    competitorSearchUrl: '/competitor-search',
    publicInsightsUrl: '/public-insights',
    upvoteUrl: '/upvotes',
    upvoteSummaryUrl: '/upvotes-summary',
    teamUrl: '/team-members',
    permissionUrl: '/permissions',
    deleteUserUrl: '/delete-user',
    adsUrl: '/insights/ads',
    mailUrl: '/insights/email',
    websiteUrl: '/insights/website',
    exportReportUrl: '/export-report',
    datasourceUrl: '/datasources?businessId=',
    datastoreUploadUrl: '/datastore-upload',
    invitedUserUrl: '/invited-user',
    reportsResyncUrl: '/reports-resync',
    billingInfoUrl: '/billing-info',
    subscriptionUrl: '/subscription',
    couponUrl: '/coupons',
    onboardingBillingUrl: '/onboarding-billing',
    metrics: '/metrics',
    datasourceMetrics: '/datasource-metrics',
    newInsights: '/new-insights',
    charts: '/charts',
    destinations: '/destinations',
    destinationProviders: '/destination-providers',
    currencyUrl: '/currency',
    subAccountsUrl: '/sub-accounts',
    articals: '/articles',
    copyTemplateUrl: '/copyTemplate',
    paddleSubscriptionUrl: '/paddle-subscriptions',
  };
};

export const CLIENT_BASE_URL_VALUES: { [env: string]: string } = {
  local: 'http://localhost:4200',
  development: 'https://staging.viewmetrics.com',
  staging: 'https://staging.viewmetrics.com',
  production: 'https://app.viewmetrics.com',
};

export const CLIENT_BASE_URL = CLIENT_BASE_URL_VALUES[environment];

export const SUPPORTED_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/png'];

export const CHANNELS: Utils.DynamicObject<string, string> = {
  facebook: 'facebook',
  instagram: 'instagram',
  twitter: 'twitter',
  youtube: 'youtube',
  linkedin: 'linkedin',
  tiktok: 'tiktok',
  pinterest: 'pinterest',
  googleMyBusiness: 'googleMyBusiness',
  facebookAds: 'facebookAds',
  linkedinAds: 'linkedinAds',
  spotifyAds: 'spotifyAds',
  bingAds: 'bingAds',
  googleAds: 'googleAds',
  youtubeAds: 'youtubeAds',
  quoraAds: 'quoraAds',
  redditAds: 'redditAds',
  twitterAds: 'twitterAds',
  mailchimp: 'mailchimp',
  hubspot: 'hubspot',
  mailshake: 'mailshake',
  pipedrive: 'pipedrive',
  salesforce: 'salesforce',
  activeCampaign: 'activeCampaign',
  constantContact: 'constantContact',
  semRush: 'semRush',
  googleAnalytics: 'googleAnalytics',
  googleAnalytics4: 'googleAnalytics4',
  seoMonitor: 'seoMonitor',
  shopify: 'shopify',
  bigCommerce: 'bigCommerce',
  wooCommerce: 'wooCommerce',
  tiktokAds: 'tiktokAds',
  echarge: 'echarge',
  brevo: 'brevo',
  lemList: 'lemlist',
  gmass: 'gmass',
  drip: 'drip',
  convertKit: 'convertKit',
  googleSearchConsole: 'googleSearchConsole',
  plausible: 'plausible',
  klaviyo: 'klaviyo',
  close: 'otherClose',
  zoho: 'zoho',
  adobeAnalytics: 'adobeAnalytics',
  jsonApi: 'jsonApi',
  googleSheet: 'googleSheet',
  flodesk: 'flodesk',
};

export const CHANNEL_GROUP = {
  social: 'social',
  ads: 'ads',
  emails: 'emails',
  website: 'website',
  ecommerce: 'ecommerce',
  all: 'all',
  coming_soon: 'Coming Soon',
};

export const TEMPLATE_CHANNEL_GROUP = {
  socials: 'social',
  ads: 'ads',
  emails: 'emails',
  websites: 'website',
};

export const CHANNELS_BY_GROUP = {
  [CHANNEL_GROUP.social]: [
    CHANNELS.facebook,
    CHANNELS.instagram,
    CHANNELS.youtube,
    CHANNELS.linkedin,
    CHANNELS.tiktok,
  ],
  [CHANNEL_GROUP.ads]: [CHANNELS.facebookAds, CHANNELS.googleAds],
  [CHANNEL_GROUP.emails]: [
    CHANNELS.mailchimp,
    CHANNELS.activeCampaign,
    CHANNELS.constantContact,
    CHANNELS.hubspot,
  ],
  [CHANNEL_GROUP.website]: [
    // CHANNELS.googleAnalytics,
    CHANNELS.googleAnalytics4,
    CHANNELS.googleSearchConsole,
  ],
  [CHANNEL_GROUP.coming_soon]: [
    CHANNELS.twitter,
    CHANNELS.pinterest,
    CHANNELS.googleMyBusiness,
    CHANNELS.linkedinAds,
    CHANNELS.spotifyAds,
    CHANNELS.bingAds,
    CHANNELS.youtubeAds,
    CHANNELS.quoraAds,
    CHANNELS.redditAds,
    CHANNELS.twitterAds,
    CHANNELS.mailshake,
    CHANNELS.pipedrive,
    CHANNELS.salesforce,
    CHANNELS.semRush,
    CHANNELS.seoMonitor,
    CHANNELS.shopify,
    CHANNELS.bigCommerce,
    CHANNELS.wooCommerce,
    CHANNELS.tiktokAds,
    CHANNELS.echarge,
    CHANNELS.brevo,
    CHANNELS.lemList,
    CHANNELS.gmass,
    CHANNELS.drip,
    CHANNELS.convertKit,
    CHANNELS.plausible,
    CHANNELS.klaviyo,
    CHANNELS.close,
    CHANNELS.zoho,
    CHANNELS.adobeAnalytics,
    CHANNELS.jsonApi,
    CHANNELS.googleSheet,
    CHANNELS.flodesk,
  ],
  [CHANNEL_GROUP.ecommerce]: [],
  [CHANNEL_GROUP.all]: Object.values(CHANNELS),
};

export const ENABLED_CHANNELS = [
  CHANNELS.facebook,
  CHANNELS.instagram,
  CHANNELS.youtube,
  CHANNELS.linkedin,
  CHANNELS.tiktok,
  CHANNELS.googleAds,
  CHANNELS.mailchimp,
  CHANNELS.facebookAds,
  // CHANNELS.googleAnalytics,
  CHANNELS.googleAnalytics4,
  CHANNELS.googleSearchConsole,
  CHANNELS.activeCampaign,
  CHANNELS.constantContact,
];

export const BACKEND_PROVIDERS = {
  [CHANNELS.facebook]: 'facebook',
  [CHANNELS.instagram]: 'facebook',
  [CHANNELS.twitter]: 'twitter',
  [CHANNELS.youtube]: 'youtube',
  [CHANNELS.linkedin]: 'linkedin2',
  [CHANNELS.tiktok]: 'tiktok',
  [CHANNELS.googleAds]: 'google',
  [CHANNELS.mailchimp]: 'mailchimp',
  [CHANNELS.facebookAds]: 'facebook',
};

export const ONBOARDING = {
  businessRoles,
  employeeCounts,
  industries,
  subscriptions,
  INVITED_STEP: 'invited',
  WELCOME_STEP: 'welcome',
  PROFILE_STEP: 'profile',
  BUSINESS_STEP: 'business',
  WORKSPACE_STEP: 'workspace',
  ADD_CLIENT_STEP: 'add-client',
  BILLING_STEP: 'billing',
  COMPLETE_STEP: 'complete',
};

export const PAGINATION_PER_PAGE_LIMITS = {
  // TODO: will fix it later
  PAST_REPORTS_LIMIT: 100,
};

export const HELPER_ARTICLES = {
  MISSING_ANALYTICS_DATA:
    'https://support.viewmetrics.com/en/article/why-do-i-have-missing-data-i3a3si/',
  REFRESH_ACCESS_TOKEN: '5ef8e4ed2c7d3a10cba9ae3c',
  MISSING_PERMISSIONS: '5ef90dd204286306f805dfe4',
  CONNECTING_INSTAGRAM_ACCOUNT:
    'https://support.viewmetrics.com/en/article/connecting-your-instagram-account-1cwomcs/',
  MISSING_DEMOGRAPHIC_DATA:
    'https://support.viewmetrics.com/en/article/missing-demographic-data-qtinrq/',
  EMBED_CHART_LINK:
    'https://support.viewmetrics.com/en/article/why-are-embedded-charts-better-than-screenshots-18gskje/',
  MONTHLY_GOALS_INFO: '5f0a08e504286306f8068bf1',
  UPDATE_ACCOUNT: '6008643ec64fe14d0e1fcbe2',
  ADD_CLIENT: '60007f6fb9a8501b295d07fc',
  CREATE_REPORT: '6000a55ac64fe14d0e1fb05d',
  ADD_TEAMMATE: '6000af002e764327f87bed28',
  TEAMMEMBER_ROLES_INFO: '6000b0b3c64fe14d0e1fb0c0',
  REPORT_REFRESH: '6062a8f90ad05379b10d200f',
};

export const APP_ROUTE = {
  ROOT: '/',
  SIGN_UP: 'signup',
  LOGIN: 'login',
  AUTH: 'auth',
  ONBOARDING: 'onboarding',
  WELCOME: 'welcome',
  BUSINESS: 'business',
  ADD_CLIENT: 'add-client',
  INVITED: 'invited',
  DEMO: 'demo',
  ANALYTICS: 'analytics',
  OVERVIEW: 'overview',
  AUDIENCE: 'audience',
  PROFILE: 'profile',
  PAGE_INSIGHTS: 'page-insights',
  POSTS: 'posts',
  COMPETITION: 'competition',
  HOME: 'home',
  REPORTING: 'reporting',
  REPORTS: 'reports',
  CREATE: 'create',
  EMAIL: 'email',
  WEBSITE: 'website',
  SETTINGS: 'settings',
  CLIENT: 'client',
  ACCOUNT: 'account',
  BILLING: 'billing',
  TEAM: 'team',
  LOGOUT: 'logout',
  SOCIAL_AUTH: 'social-auth',
  EXTERNAL_AUTH: 'external-auth',
  TEMPLATES: 'templates',
  DASHBOARD: 'dashboard',
  ADS: 'ads',
  DATA_SOURCES: 'datasources',
  CONNECTIONS: 'connections',
  CHARTS: 'charts',
  WORKSPACE: 'workspace',
  ADDON: 'addon',
  ADD: 'add',
  SUPPORT: 'support',
  SOCIAL: 'social',
  CHART: 'chart',
  EMBED: 'embed',
  TEMPLATE: 'template',
  DESTINATION: 'destinations',
  CURRENCY: 'currency',
  LIST: 'list',
  SUB_ACCOUNTS: 'sub-accounts',
  VERIFICATION: 'verification',
  VERIFY_EMAIL: 'verify-email',
  RESEND_VERIFICATION_EMAIL: 'resendVerificationEmail',
  PROMO_CODES: 'promo-codes',
  FORGOT_PASSWORD: 'resetpassword',
  UPDATE_PASSWORD: 'updatepassword',
  ADSETS: 'adsets',
  PAYMENT_CONFIRM: 'payment-confirm',
  CHECKOUT: 'checkout',
} as const;

export const subscriptionStatus = {
  TRIAL: 'in_trial',
  ACTIVE: 'active',
  CANCELLED: 'cancelled',
};

export const subscriptionPlan = {
  BASIC: 'basic',
  PRO: 'pro',
  AGENCY: 'plan-agency',
  TEAM: 'team-monthly',
};

export const survey = {
  ADD_SERVICE: 'new-services-to-add',
};

export const hiddenTemplates = ['Youtube Dashboard Report'];

// eslint-disable-next-line no-shadow
export enum LOCAL_STORAGE_TYPES {
  WORKSPACE = 'workspace',
  USERID = 'userid',
  DELETED_TEAM_MEMBER = 'deleted_team_member',
  ADDON = 'addon',
  ADS_CAMPAIGNS = 'ads_campaigns',
  EMAIL_CAMPAIGNS = 'email_campaigns',
  CRISP_FEEDBACK = 'crisp_feedback',
  INVITATION_TOKEN = 'invitation_token',
  INVITED_BUSINESS_ID = 'invited_business_id',
  SOCIAL_AUTH_TOKEN = 'social_auth_token',
  RESEND_MAIL_TIMEOUT = 'resend_mail_timeout',
}
export const PERIOD_OPTIONS: Utils.DynamicObject<string, string> = {
  lastWeek: 'Last week',
  '1week': '1week',
  last7Days: 'Last 7 days',
  last7days: 'Last 7 days',
  last14Days: 'Last 14 days',
  last14days: 'Last 14 days',
  last28Days: 'Last 28 days',
  last28days: 'Last 28 days',
  lastMonth: 'Last month',
  lastQuarter: 'Last quarter',
  custom: 'Custom',
  lifetime: 'lifetime',
  yesterday: 'yesterday',
  day: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  year: 'year',
};

export const newClientPage = {
  SELECT_CHANNELS: 'SELECT_CHANNELS',
  CLIENT_DETAILS: 'CLIENT_DETAILS',
  CREATING_CLIENT: 'CREATING_CLIENT',
  SUCCESS: 'SUCCESS',
};

export const channelTab = {
  SEARCH: 'search',
  SOCIAL: CHANNEL_GROUP.social,
  ADS: CHANNEL_GROUP.ads,
  EMAILS: CHANNEL_GROUP.emails,
  WEBSITE: CHANNEL_GROUP.website,
  COMING_SOON: CHANNEL_GROUP.coming_soon,
};

export const mapChannelTabToLabel = {
  [channelTab.SOCIAL]: 'Social media',
  [channelTab.ADS]: 'Ad accounts',
  [channelTab.EMAILS]: 'Emails',
  [channelTab.WEBSITE]: 'Website',
  [channelTab.SEARCH]: 'Search result',
};

export const creatingStep = {
  CONNECT: 'Connecting to',
  PULL: 'Pulling analytics data',
  INVITES: 'Sending invites to users',
};

export const columnName = {
  COMPETITOR: 'competitor',
  FOLLOWERS: 'followers',
  POSTS: 'posts',
  LIKES: 'likes',
  COMMENTS: 'comments',
  AVG_ENG_RATE: 'avgEngRate',
  CONVERSATION: 'conversation',
  ENGAGEMENT_RATE: 'engagementRate',
};

export const checkNonAverageMetric = (metric: string): boolean => {
  return /cpm|cpc|ctr|cost|rate|per/gi.test(metric);
};

export const ERROR_MESSAGE = {
  status: 'Network Error',
  msg:
    "We're having trouble connecting to ViewMetrics. Please check your internet connection or try again later.",
};

export const LINKS = {
  FILTER:
    'https://support.viewmetrics.com/en/article/filter-dashboard-data-q98wu9/',
};

export const SPARROW_ADDON_MARKET_PLACE_URL =
  'https://workspace.google.com/u/0/marketplace/app/sparrow/884437570161';

export const SPARROW_CHARTS_SUPPORT = 'https://support.viewmetrics.com';

export const TIME_RANGE_MAPPING: Utils.DynamicObject<string, string> = {
  'Last week': '1week',
  'Last 7 days': 'last7days',
  'Last 14 days': 'last14days',
  'Last 28 days': 'last28days',
  'Last month': '1month',
  'Last quarter': '3month',
  'Last 6 months': '6month',
  'last 12 months': '12month',
  'last 24 months': '24month',
  'Last year': '1year',
  Custom: 'custom',
  Lifetime: 'lifetime',
  '1week': 'Last week',
  last7days: 'Last 7 days',
  last14days: 'Last 14 days',
  last28days: 'Last 28 days',
  '1month': 'Last month',
  '3month': 'Last quarter',
  '6month': 'Last 6 months',
  '12month': 'last 12 months',
  '24month': 'last 24 months',
  '1year': 'Last year',
  custom: 'Custom',
  lifetime: 'Lifetime',
};

/**
 * This is to ensure that the onboarding steps are correctly mapped to the app routes
 */

if (ONBOARDING.INVITED_STEP !== APP_ROUTE.INVITED) {
  throw new Error('ONBOARDING.INVITED_STEP is not equal to APP_ROUTE.INVITED');
}

if (ONBOARDING.WELCOME_STEP !== APP_ROUTE.WELCOME) {
  throw new Error('ONBOARDING.WELCOME_STEP is not equal to APP_ROUTE.WELCOME');
}

if (ONBOARDING.PROFILE_STEP !== APP_ROUTE.PROFILE) {
  throw new Error('ONBOARDING.PROFILE_STEP is not equal to APP_ROUTE.PROFILE');
}

if (ONBOARDING.BUSINESS_STEP !== APP_ROUTE.BUSINESS) {
  throw new Error(
    'ONBOARDING.BUSINESS_STEP is not equal to APP_ROUTE.BUSINESS'
  );
}

if (ONBOARDING.WORKSPACE_STEP !== APP_ROUTE.WORKSPACE) {
  throw new Error(
    'ONBOARDING.WORKSPACE_STEP is not equal to APP_ROUTE.WORKSPACE'
  );
}

if (ONBOARDING.ADD_CLIENT_STEP !== APP_ROUTE.ADD_CLIENT) {
  throw new Error(
    'ONBOARDING.ADD_CLIENT_STEP is not equal to APP_ROUTE.ADD_CLIENT'
  );
}

if (ONBOARDING.BILLING_STEP !== APP_ROUTE.BILLING) {
  throw new Error('ONBOARDING.BILLING_STEP is not equal to APP_ROUTE.BILLING');
}

export const COOKIE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
};
